const W = 1024;
const H = 400;

function renderTemplate(template, ctx) {
  if (!template) return '';
  return template
    .replaceAll('{user}', ctx.username)
    .replaceAll('{username}', ctx.username)
    .replaceAll('{tag}', ctx.tag)
    .replaceAll('{server}', ctx.server)
    .replaceAll('{memberCount}', String(ctx.memberCount));
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function fitText(ctx, text, maxWidth, baseSize, family) {
  let size = baseSize;
  ctx.font = `${size}px ${family}`;
  while (ctx.measureText(text).width > maxWidth && size > 12) {
    size -= 2;
    ctx.font = `${size}px ${family}`;
  }
  return size;
}

export function clampOffset(offset, canvasDim, scaledDim) {
  if (scaledDim <= canvasDim) return 0;
  const half = (scaledDim - canvasDim) / 2;
  return Math.max(-half, Math.min(half, offset));
}

export function computeBgGeometry(imgW, imgH, userScale) {
  const coverScale = Math.max(W / imgW, H / imgH);
  const scale = coverScale * Math.max(1, userScale || 1);
  return { dw: imgW * scale, dh: imgH * scale };
}

async function drawBackground(ctx, bgUrl, bgCfg) {
  if (bgUrl) {
    try {
      const img = await loadImage(bgUrl);
      const { dw, dh } = computeBgGeometry(img.width, img.height, bgCfg.scale);
      const offX = clampOffset(bgCfg.offsetX || 0, W, dw);
      const offY = clampOffset(bgCfg.offsetY || 0, H, dh);
      ctx.drawImage(img, (W - dw) / 2 + offX, (H - dh) / 2 + offY, dw, dh);
      const darken = Math.max(0, Math.min(1, bgCfg.darken ?? 0.35));
      if (darken > 0) {
        ctx.fillStyle = `rgba(0,0,0,${darken})`;
        ctx.fillRect(0, 0, W, H);
      }
      return;
    } catch { /* fallback below */ }
  }
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#3b3f5e');
  grad.addColorStop(1, '#1b1d2c');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
}

async function drawAvatar(ctx, url, cx, cy, r) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r + 4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,.9)';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  try {
    const img = await loadImage(url);
    ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  } catch {
    ctx.fillStyle = '#5865f2';
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  ctx.restore();
}

export async function renderPreview(canvas, cfg, ctx_params) {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  await drawBackground(ctx, cfg.bgUrl, { scale: cfg.bg_scale, offsetX: cfg.bg_offset_x, offsetY: cfg.bg_offset_y, darken: cfg.bg_darken });

  const padX = 60;
  const avatarR = 96;
  const avatarCx = padX + avatarR;
  const avatarCy = H / 2;

  await drawAvatar(ctx, ctx_params.avatarUrl, avatarCx, avatarCy, avatarR);

  const textX = avatarCx + avatarR + 40;
  const maxW = W - textX - padX;
  const family = cfg.font_family || 'sans-serif';

  const title = renderTemplate(cfg.title || 'Welcome {username}!', ctx_params);
  const subtitle = renderTemplate(cfg.subtitle || '', ctx_params);

  const titleSize = fitText(ctx, title, maxW, cfg.title_size || 54, `bold ${family}`);
  ctx.font = `bold ${titleSize}px ${family}`;
  ctx.fillStyle = cfg.title_color || '#ffffff';
  ctx.textBaseline = 'alphabetic';
  ctx.shadowColor = 'rgba(0,0,0,.5)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;

  const titleY = subtitle ? H / 2 : H / 2 + titleSize / 3;
  ctx.fillText(title, textX, titleY);

  if (subtitle) {
    const subSize = fitText(ctx, subtitle, maxW, cfg.subtitle_size || 24, family);
    ctx.font = `${subSize}px ${family}`;
    ctx.fillStyle = cfg.subtitle_color || '#dddddd';
    ctx.shadowBlur = 4;
    ctx.fillText(subtitle, textX, titleY + subSize + 16);
  }

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.strokeStyle = 'rgba(255,255,255,.06)';
  ctx.lineWidth = 2;
  drawRoundedRect(ctx, 8, 8, W - 16, H - 16, 16);
  ctx.stroke();
}

export const CARD_DIMS = { W, H };
